import React from "react"
import Card from "react-bootstrap/Card"

import { GetApi } from "../../../api/api"
import { Project, FeatureCatalog, AttributeTypeGroup, CustomFeatureType } from "../../../api/types"
import Pending from "../../../components/Pending"
import withPending from "../../../components/HOC/WithPending"

import PropertySets from "./PropertySets/PropertySets"
import FeatureTypes from "./FeatureTypes/FeatureTypes"
import PublishedVersions from "./PublishedVersions"
import { CustomFeatureCatalog } from "../../../api/generated/QuadriConfigurationApi"
import { useHistory } from "react-router-dom"

interface InnerPanelProps {
  id: string
  onRefresh: () => void
  disabled: boolean
  setDisabled: (value: boolean) => void
}

interface RenderSuccess {
  featureCatalog: Project
  baseFeatureCatalogs: Array<{ name: string; id: string }>
  customFeatureTypes: CustomFeatureType[]
  propertySets: AttributeTypeGroup[]
  customFeatureCatalogs: CustomFeatureCatalog[]
}

const renderSuccess = (props: InnerPanelProps & RenderSuccess) => (
  <React.Fragment>
    <div className="panel-header">
      <h5>Edit Feature Catalog</h5>
    </div>

    <div className="panel-body">
      <div className="scroll-container p-2">
        <Card className="mt-2">
          <FeatureTypes
            featureCatalog={props.featureCatalog}
            customFeatureTypes={props.customFeatureTypes}
            refresh={props.onRefresh}
            disabled={props.disabled}
            setDisabled={props.setDisabled}
          />
        </Card>
        <Card className="mt-2">
          <PropertySets
            featureCatalog={props.featureCatalog}
            propertySets={props.propertySets}
            refresh={props.onRefresh}
            disabled={props.disabled}
            setDisabled={props.setDisabled}
          />
        </Card>
        <Card className="mt-2">
          {props.customFeatureCatalogs.length !== 0 ? (
            <PublishedVersions history={useHistory()} customFeatureCatalogs={props.customFeatureCatalogs} />
          ) : null}
        </Card>
      </div>
    </div>
  </React.Fragment>
)

const getData = async (props: InnerPanelProps) => {
  try {
    const api = await GetApi()
    // Make as many requests in parallel as possible
    const [fcc, customFeatureTypes, propertySets, customFeatureCatalogs] = await Promise.all([
      api.getFeatureCatalogCustomization(props.id),
      api.getCustomFeatureTypes(props.id),
      api.getAttributeTypeGroups(props.id),
      api.getCustomFeatureCatalogs(props.id)
    ])

    // if the feature catalog has ever been published no standard is required
    // if the feature catalog has been published, assume one base, and show only compatible standard and min version
    let featureCatalogs: FeatureCatalog[]
    if (fcc.version && fcc.version > 1) {
      const base = fcc.baseFeatureCatalogs[0]
      featureCatalogs = await api.getFeatureCatalogs(base.standard, base.version)
    } else {
      featureCatalogs = await api.getFeatureCatalogs()
    }
    return {
      featureCatalog: fcc,
      baseFeatureCatalogs: featureCatalogs.map((fc) => ({
        name: `${fc.name} ${fc.version} - ${fc.date}`,
        id: fc.id
      })),
      customFeatureTypes,
      propertySets,
      customFeatureCatalogs
    }
  } catch (error) {
    throw new Error(error as any)
  }
}

const InnerPanel = withPending(renderSuccess, getData, {
  renderPendingOnRefresh: true
})

type PendingType = Pending<InnerPanelProps & RenderSuccess>

interface PanelProps {
  id: string
  onRefresh: (id: string) => void
}

interface PanelState {
  disabled: boolean
}

class Panel extends React.Component<PanelProps, PanelState> {
  private pendingRef: React.RefObject<PendingType>

  constructor(props: PanelProps) {
    super(props)
    this.pendingRef = React.createRef<PendingType>()
    this.state = {
      disabled: false
    }
  }

  public render() {
    return (
      <InnerPanel
        ref={this.pendingRef}
        id={this.props.id}
        onRefresh={this.onRefresh}
        disabled={this.state.disabled}
        setDisabled={this.setDisabled}
      />
    )
  }

  private onRefresh = () => {
    this.props.onRefresh(this.props.id)
    this.pendingRef?.current?.refresh()
  }

  private setDisabled = (value: boolean) => {
    this.setState({
      disabled: value
    })
  }
}

export default Panel
